import { Field, FieldProps, Form, Formik, FormikTouched } from "formik";
import { DateTime } from "luxon";
import { ChangeEvent, MouseEventHandler, useCallback, useState } from "react";
import { useDispatch } from "react-redux";

import { Location, PatientGuarantor } from "pwapi";

import {
  updatePatientGuarantor,
  createPatientGuarantor,
} from "shared/actions/admin/adminPatientActions";
import {
  patientGuarantorAdminSchema,
  patientGuarantorWarningSchema,
} from "shared/utils/caseSchema";
import { locationOptions } from "shared/utils/usRegions";
import {
  canValidate,
  getErrors,
  getValidationWarnings,
  getValueFromObject,
} from "shared/utils/validationUtils";

import { PathologyWatchButton } from "components/customInputs/buttons/Button";
import CustomInput from "components/customInputs/CustomInput";
import DateInput from "components/customInputs/DateInput";
import SelectInput from "components/customInputs/SelectInput";
import TextInput from "components/customInputs/TextInput";

import {
  initialValuesForPatientGuarantor,
  shallowPatientGuarantorComparison,
} from "./guarantorFormUtils";
import FormErrors from "../../editCasePanel/FormErrors";

type GuarantorFormProps = {
  accessionCompleted?: string | null;
  guarantor: PatientGuarantor;
  patientId: number;
  onClose: MouseEventHandler<HTMLButtonElement>;
  disableSubmit?: boolean;
  patientAddressValues?: Location;
};

const GuarantorForm = ({
  accessionCompleted,
  guarantor,
  patientId,
  onClose,
  disableSubmit = false,
  patientAddressValues = {},
}: GuarantorFormProps) => {
  const dispatch = useDispatch();
  const [warnings, setWarnings] = useState<{ [key: string]: string }>({});
  const initialValues = initialValuesForPatientGuarantor(guarantor);

  const handleOnSubmit = useCallback(
    (values: PatientGuarantor) => {
      if (disableSubmit) return;

      const payload = {
        id: guarantor?.id,
        patient_id: patientId,
        onClose,
        ...shallowPatientGuarantorComparison(guarantor, values),
      };
      guarantor.id
        ? dispatch(updatePatientGuarantor(payload))
        : patientId && dispatch(createPatientGuarantor(payload));
    },
    [patientId, guarantor]
  );

  const canValidateForm = canValidate(
    guarantor?.patient?.clinic?.customer_type
  );

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      onSubmit={handleOnSubmit}
      validate={(values: any) => {
        canValidateForm &&
          patientGuarantorWarningSchema
            .validate(values, {
              abortEarly: false,
              context: { address: patientAddressValues },
            })
            .then(() => {
              setWarnings({});
            })
            .catch((err) => {
              setWarnings(getValidationWarnings(err));
            });
      }}
      validateOnBlur={false}
      validationSchema={canValidateForm ? patientGuarantorAdminSchema : null}
      validateOnMount={true}
    >
      {({
        resetForm,
        dirty,
        errors,
        touched,
        values,
        initialValues,
        setValues,
      }) => {
        // Unpack touched address fields for typescript
        const addressTouched: FormikTouched<Location> | undefined =
          touched.address as unknown as FormikTouched<Location>;

        // Determine if any address fields have been touched for universal address warning
        const isAddressTouched =
          addressTouched?.street ||
          addressTouched?.street2 ||
          addressTouched?.city ||
          addressTouched?.state ||
          addressTouched?.zipcode;

        const allErrors = getErrors(errors, touched, !accessionCompleted, [
          "address.phone1",
          "address.phone2",
          "address.street",
          "address.zipcode",
          "date_of_birth",
          "ssn",
        ]);
        const allWarnings = getErrors(
          warnings,
          { ...touched, address: isAddressTouched },
          !accessionCompleted
        );

        return (
          <Form className="AdminPanelForm">
            <div className="panelContainer">
              <fieldset className="panelContainer__section">
                <legend className="panelContainer__section--title">
                  Guarantor Information
                </legend>
                <div className="panelContainer__inputGroup">
                  <Field name="relationship_to_patient">
                    {({ field }: any) => (
                      <SelectInput
                        label="Sub Relationship"
                        options={["SELF", "SPOUSE", "PARENT", "OTHER"].map(
                          (a) => ({
                            label: a,
                            value: a,
                          })
                        )}
                        {...field}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                          setValues({
                            // Only reset values if the user is swapping from SELF to something else, or something else to SELF.
                            ...((values.relationship_to_patient === "SELF" &&
                              e.target.value !== "SELF") ||
                            (values.relationship_to_patient !== "SELF" &&
                              e.target.value === "SELF")
                              ? Object.keys(initialValues).reduce(
                                  (acc: any, key) => {
                                    return key === "address"
                                      ? { ...acc, [key]: {} }
                                      : { ...acc, [key]: "" };
                                  },
                                  {}
                                )
                              : values),
                            relationship_to_patient: e.target.value,
                          });
                        }}
                        disabled={disableSubmit}
                      />
                    )}
                  </Field>
                  <Field name="last_name">
                    {({ field }: FieldProps) => (
                      <TextInput
                        label="Last Name"
                        id="guarantor_last_name"
                        {...field}
                        disabled={
                          disableSubmit ||
                          values.relationship_to_patient === "SELF"
                        }
                      />
                    )}
                  </Field>
                  <Field name="first_name">
                    {({ field }: FieldProps) => (
                      <TextInput
                        label="First Name"
                        id="guarantor_first_name"
                        required
                        {...field}
                        disabled={
                          disableSubmit ||
                          values.relationship_to_patient === "SELF"
                        }
                      />
                    )}
                  </Field>
                  <Field name="middle_initial">
                    {({ field }: FieldProps) => (
                      <TextInput
                        label="M.I."
                        id="guarantor_middle_initial"
                        maxLength={2}
                        {...field}
                        disabled={
                          disableSubmit ||
                          values.relationship_to_patient === "SELF"
                        }
                      />
                    )}
                  </Field>
                </div>
                <div className="panelContainer__inputGroup">
                  <Field name="ssn">
                    {({ field }: FieldProps) => (
                      <TextInput
                        label="SSN"
                        id="ssn"
                        {...field}
                        disabled={
                          disableSubmit ||
                          values.relationship_to_patient === "SELF"
                        }
                        showErrorText={false}
                        error={getValueFromObject(allErrors, "ssn")}
                      />
                    )}
                  </Field>
                  <Field name="date_of_birth">
                    {({ field, form }: any) => (
                      <CustomInput
                        label="Date of birth"
                        id="date_of_birth"
                        showErrorText={false}
                        error={getValueFromObject(allErrors, "date_of_birth")}
                        warning={getValueFromObject(
                          allWarnings,
                          "date_of_birth"
                        )}
                      >
                        <DateInput
                          {...field}
                          onChange={(e: ChangeEvent<HTMLInputElement>) =>
                            form.setFieldValue("date_of_birth", e.target.value)
                          }
                          disabled={
                            disableSubmit ||
                            values.relationship_to_patient === "SELF"
                          }
                          max={DateTime.now().toISODate()}
                        />
                      </CustomInput>
                    )}
                  </Field>
                  <Field name="sex">
                    {({ field }: FieldProps) => (
                      <div style={{ width: "4em", flex: "0 0 auto" }}>
                        <SelectInput
                          id="sex"
                          label="Sex"
                          {...field}
                          options={Object.values(PatientGuarantor.sex).map(
                            (s) => ({
                              label: s,
                              value: s,
                            })
                          )}
                          disabled={
                            disableSubmit ||
                            values.relationship_to_patient === "SELF"
                          }
                        />
                      </div>
                    )}
                  </Field>
                </div>
              </fieldset>
              <fieldset className="panelContainer__section">
                <legend className="panelContainer__section--title">
                  Guarantor Contact Information
                </legend>
                <div className="panelContainer__inputGroup">
                  <Field name="address.phone1">
                    {({ field }: FieldProps) => (
                      <TextInput
                        type="tel"
                        label="Phone"
                        id="guarantor_phone1"
                        {...field}
                        disabled={
                          disableSubmit ||
                          values.relationship_to_patient === "SELF"
                        }
                        showErrorText={false}
                        error={allErrors["address.phone1"] || ""}
                      />
                    )}
                  </Field>
                  <Field name="address.phone2">
                    {({ field }: FieldProps) => (
                      <TextInput
                        type="tel"
                        label="Phone 2"
                        id="guarantor_phone2"
                        {...field}
                        disabled={
                          disableSubmit ||
                          values.relationship_to_patient === "SELF"
                        }
                        showErrorText={false}
                        error={allErrors["address.phone2"] || ""}
                      />
                    )}
                  </Field>
                </div>
              </fieldset>
              <fieldset className="panelContainer__section">
                <legend className="panelContainer__section--title">
                  Guarantor Address
                </legend>
                <PathologyWatchButton
                  type="button"
                  onClick={() => {
                    const tempAddress = {
                      street: patientAddressValues.street || "",
                      street2: patientAddressValues.street2 || "",
                      city: patientAddressValues.city || "",
                      state: patientAddressValues.state || "",
                      zipcode: patientAddressValues.zipcode || "",
                    };
                    setValues({
                      ...values,
                      address: tempAddress,
                    });
                  }}
                  disabled={
                    disableSubmit ||
                    !patientAddressValues ||
                    !Object.keys(patientAddressValues).filter((s) => s)
                      .length ||
                    values.relationship_to_patient === "SELF"
                  }
                >
                  Use Patient Address
                </PathologyWatchButton>
                <Field name="address.street">
                  {({ field }: FieldProps) => (
                    <TextInput
                      label="Street Line 1"
                      id="guarantor_street"
                      {...field}
                      disabled={
                        disableSubmit ||
                        values.relationship_to_patient === "SELF"
                      }
                    />
                  )}
                </Field>
                <Field name="address.street2">
                  {({ field }: FieldProps) => (
                    <TextInput
                      label="Street Line 2"
                      id="guarantor_street2"
                      {...field}
                      disabled={
                        disableSubmit ||
                        values.relationship_to_patient === "SELF"
                      }
                    />
                  )}
                </Field>
                <div className="panelContainer__inputGroup">
                  <Field name="address.city">
                    {({ field }: FieldProps) => (
                      <TextInput
                        label="City"
                        id="city"
                        {...field}
                        disabled={
                          disableSubmit ||
                          values.relationship_to_patient === "SELF"
                        }
                      />
                    )}
                  </Field>
                  <Field name="address.state">
                    {({ field }: FieldProps) => (
                      <SelectInput
                        label="State"
                        id="guarantor_state"
                        options={locationOptions}
                        {...field}
                        value={field.value || ""}
                        disabled={
                          disableSubmit ||
                          values.relationship_to_patient === "SELF"
                        }
                      />
                    )}
                  </Field>
                  <Field name="address.zipcode">
                    {({ field }: FieldProps) => (
                      <TextInput
                        label="Zip Code"
                        id="guarantor_zipcode"
                        {...field}
                        disabled={
                          disableSubmit ||
                          values.relationship_to_patient === "SELF"
                        }
                        showErrorText={false}
                        error={allErrors["address.zipcode"] || ""}
                      />
                    )}
                  </Field>
                </div>
              </fieldset>
              <FormErrors errors={allErrors} warnings={allWarnings} />
            </div>
            <footer>
              <div className="panelContainer__inputGroup">
                <PathologyWatchButton onClick={onClose}>
                  Cancel
                </PathologyWatchButton>
              </div>

              <div className="panelContainer__inputGroup">
                <PathologyWatchButton
                  onClick={() => {
                    resetForm();
                  }}
                  disabled={!dirty}
                >
                  Discard Changes
                </PathologyWatchButton>
                <PathologyWatchButton
                  type="submit"
                  color="green"
                  disabled={
                    !dirty || disableSubmit || Object.keys(allErrors).length > 0
                  }
                >
                  Save
                </PathologyWatchButton>
              </div>
            </footer>
          </Form>
        );
      }}
    </Formik>
  );
};

export default GuarantorForm;
